﻿import {
  Fragment,
  useEffect,
  useId,
  useMemo,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { Cable, ChevronDown, ChevronRight, ExternalLink, Plus, Search as SearchIcon, Target, Trash2 } from 'lucide-react';
import { nanoid } from 'nanoid';
import { useStore, activeSheet } from '../store';
import { harnessColor } from '../harnessColor';
import { HarnessDiagram } from './HarnessDiagram';
import { openPopoutWindow } from '../windowSync';
import type { HarnessGraph, HarnessGraphNode, InterconnectBundle } from '../types';
import {
  computeHarnessWires,
  computeHarnessEndpoints,
  endpointKey,
  findConnectorNode,
  upsertConnectorNode,
  type HarnessWire,
  type HarnessEndpoint,
} from '../harnessDerive';

/* ------------------------------------------------------------------ *
 * Derivation: roll up every `harnessId`-tagged wire across all
 * schematic sheets into per-harness endpoint + pin information.
 * (Carried over verbatim from the previous implementation — this is
 * the single source of truth that both the auto-layout and the pin
 * tables read from.)
 * ------------------------------------------------------------------ */

const EMPTY_GRAPH: HarnessGraph = { nodes: [], segments: [], notes: [] };

function normalizeGraph(graph: HarnessGraph | undefined): HarnessGraph {
  if (!graph) return EMPTY_GRAPH;
  return {
    nodes: graph.nodes ?? [],
    segments: graph.segments ?? [],
    notes: graph.notes ?? [],
    tables: graph.tables ?? [],
    images: graph.images ?? [],
    callouts: graph.callouts ?? [],
  };
}

interface ConnectorRow {
  wireNo: string;
  pin: string;
  to: string;
  color: string;
  gauge: string;
}

/** Per-connector connection rows, derived from the harness wires. Matches by
 *  the stable component node id (rename-safe), falling back to the label for
 *  legacy nodes. Shared by the details panel and the per-connector pin tables. */
function connectorRows(
  componentId: string,
  component: string,
  connector: string,
  wires: HarnessWire[],
): ConnectorRow[] {
  const isNear = (compId: string, comp: string, conn: string) =>
    (componentId ? compId === componentId : comp === component) && conn === connector;
  return wires
    .filter(
      (w) =>
        isNear(w.fromCompId, w.fromComp, w.fromConnector) ||
        isNear(w.toCompId, w.toComp, w.toConnector),
    )
    .map((w) => {
      const near = isNear(w.fromCompId, w.fromComp, w.fromConnector);
      const otherComp = near ? w.toComp : w.fromComp;
      const otherConn = near ? w.toConnector : w.fromConnector;
      const otherPin = near ? w.toPin : w.fromPin;
      return {
        wireNo: w.wireNo,
        pin: near ? w.fromPin : w.toPin,
        to: `${otherComp}${otherConn ? `.${otherConn}` : ''}${otherPin ? `.${otherPin}` : ''}`,
        color: w.color,
        gauge: w.gauge,
      };
    })
    .sort((a, b) => a.pin.localeCompare(b.pin, undefined, { numeric: true }));
}

/** Common wire-end terminations (datalist suggestions; free text allowed). */
const TERMINATION_PRESETS = [
  'Ring terminal',
  'Spade terminal',
  'Ferrule',
  'Pin contact',
  'Socket contact',
  'Butt splice',
  'Solder',
  'Bare wire',
];

/** Text input that only commits on blur/Enter, not on every keystroke.
 *  Termination/contact-PN edits persist through the store, which recomputes
 *  the whole project's wire map on every commit — committing per-character
 *  would make typing feel laggy on a project with many harnesses. */
function CommitInput({
  value,
  onCommit,
  onClick,
  ...rest
}: {
  value: string;
  onCommit: (value: string) => void;
  placeholder?: string;
  list?: string;
  style?: CSSProperties;
  onClick?: (e: ReactMouseEvent<HTMLInputElement>) => void;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  return (
    <input
      {...rest}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onClick={onClick}
      onBlur={() => {
        if (local !== value) onCommit(local);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

/* The connector list is always derived live from the schematic endpoints
 * (see computeHarnessEndpoints in ../harnessDerive) — there is no stored
 * list to keep in sync. Only termination/contact-PN metadata is persisted,
 * as a sidecar node created on first edit (see upsertConnectorNode). */

/* ================================================================== *
 * Main sheet
 * ================================================================== */

export function InterconnectSheet() {
  const sheet = useStore(activeSheet);
  const addBundle = useStore((s) => s.addBundle);
  const updateBundle = useStore((s) => s.updateBundle);
  const removeBundle = useStore((s) => s.removeBundle);
  const focusHarness = useStore((s) => s.focusHarness);
  const sheets = useStore((s) => s.sheets);

  const [search, setSearch] = useState('');

  // Subsystem names feed the From/To unit suggestions on each harness card, so
  // documenting a subsystem-to-subsystem run (Robot → Control Box) is a pick,
  // not free typing.
  const subsystemNames = useMemo(() => {
    const subs = new Set<string>();
    for (const sh of sheets) {
      const m = sh.name.match(/^(.+?)\s*[—–-]\s*(.+)$/);
      if (m) subs.add(m[1].trim());
    }
    return [...subs].sort();
  }, [sheets]);

  const wiresByHarness = useMemo(() => computeHarnessWires(sheets), [sheets]);
  const endpointsByHarness = useMemo(
    () => computeHarnessEndpoints(wiresByHarness),
    [wiresByHarness],
  );

  const all = sheet.bundles ?? [];
  const q = search.trim().toLowerCase();
  const filtered = !q
    ? all
    : all.filter((b) =>
        `${b.name} ${b.fromUnit} ${b.toUnit} ${b.conduit} ${b.cables} ${b.length}`
          .toLowerCase()
          .includes(q),
      );

  const createHarnessPage = () => {
    addBundle({ name: `Harness ${all.length + 1}`, revision: 'A' });
  };

  return (
    <div style={shellStyle}>
      {/* Shared by every From/To unit input below (datalists work by id
          document-wide). */}
      <datalist id="skema-subsystem-units">
        {subsystemNames.map((n) => (
          <option key={n} value={n} />
        ))}
      </datalist>
      <div style={topBarStyle}>
        <button onClick={createHarnessPage} style={primaryBtn} title="Add harness page">
          <Plus size={14} /> Add harness page
        </button>
        <div style={searchWrapStyle}>
          <SearchIcon size={13} style={searchIconStyle} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search harness pages..."
            style={searchInputStyle}
          />
        </div>
        <div style={{ flex: 1 }} />
        <button
          style={secondaryBtnStyle}
          onClick={() => openPopoutWindow(sheet.id)}
          title="Open this harness sheet in a separate large window"
        >
          <ExternalLink size={14} /> Open window
        </button>
        <div style={countStyle}>
          {filtered.length} of {all.length} pages
        </div>
      </div>

      <div style={workspaceStyle}>
        <HarnessOverviewTable
          bundles={filtered}
          endpointsByHarness={endpointsByHarness}
          wiresByHarness={wiresByHarness}
          onPersistGraph={(id, g) => updateBundle(id, { graph: g })}
          onChangeBundle={(id, patch) => updateBundle(id, patch)}
          onDeleteBundle={(id, name) => {
            if (window.confirm(`Delete harness page "${name}"?`)) removeBundle(id);
          }}
          onShowInSchematic={(id) => focusHarness(id)}
          onAdd={createHarnessPage}
        />
      </div>
    </div>
  );
}

/* ================================================================== *
 * Overview — master table of every harness on the sheet
 * ================================================================== */

function HarnessOverviewTable({
  bundles,
  endpointsByHarness,
  wiresByHarness,
  onPersistGraph,
  onChangeBundle,
  onDeleteBundle,
  onShowInSchematic,
  onAdd,
}: {
  bundles: InterconnectBundle[];
  endpointsByHarness: Map<string, HarnessEndpoint[]>;
  wiresByHarness: Map<string, HarnessWire[]>;
  onPersistGraph: (id: string, graph: HarnessGraph) => void;
  onChangeBundle: (id: string, patch: Partial<InterconnectBundle>) => void;
  onDeleteBundle: (id: string, name: string) => void;
  onShowInSchematic: (id: string) => void;
  onAdd: () => void;
}) {
  const fmtLen = (mm: number) => (mm >= 1000 ? `${(mm / 1000).toFixed(2)} m` : `${mm} mm`);
  // Which harness rows are expanded inline — accordion, no navigation away
  // from this table.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div style={overviewWrapStyle}>
      <table style={overviewTableStyle}>
        <thead>
          <tr>
            <th style={ovThStyle}>Harness</th>
            <th style={ovThStyle}>From → To</th>
            <th style={ovThNumStyle}>Conn.</th>
            <th style={ovThNumStyle}>Wires</th>
            <th style={ovThNumStyle}>Length</th>
            <th style={ovThStyle}>Conduit</th>
            <th style={ovThStyle}>Rev</th>
          </tr>
        </thead>
        <tbody>
          {bundles.length === 0 && (
            <tr>
              <td style={ovTdStyle} colSpan={7}>
                No harnesses yet. Click <strong>Add harness page</strong> to create one.
              </td>
            </tr>
          )}
          {bundles.map((b) => {
            const eps = endpointsByHarness.get(b.id) ?? [];
            const wires = wiresByHarness.get(b.id) ?? [];
            const totalMm = (b.graph?.segments ?? []).reduce((s, seg) => s + (seg.lengthMm ?? 0), 0);
            const isOpen = expanded.has(b.id);
            return (
              <Fragment key={b.id}>
                <tr
                  onClick={() => toggleExpanded(b.id)}
                  style={ovRowStyle}
                  title="Expand/collapse this harness"
                >
                  <td style={ovTdStyle}>
                    {isOpen ? (
                      <ChevronDown size={13} style={ovChevronStyle} />
                    ) : (
                      <ChevronRight size={13} style={ovChevronStyle} />
                    )}
                    <span style={{ ...tabAccentStyle, background: harnessColor(b.id), marginRight: 8 }} />
                    <strong>{b.name || 'Untitled harness'}</strong>
                  </td>
                  <td style={ovTdStyle}>
                    {b.fromUnit || b.toUnit ? `${b.fromUnit || '?'} → ${b.toUnit || '?'}` : '—'}
                  </td>
                  <td style={ovTdNumStyle}>{eps.length}</td>
                  <td style={ovTdNumStyle}>{wires.length}</td>
                  <td style={ovTdNumStyle}>{totalMm > 0 ? fmtLen(totalMm) : b.length || '—'}</td>
                  <td style={ovTdStyle}>{b.conduit || '—'}</td>
                  <td style={ovTdStyle}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      {b.revision || '—'}
                      <button
                        style={showInSchematicBtnStyle}
                        title="Show this harness in the schematic (highlights its wires)"
                        disabled={wires.length === 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          onShowInSchematic(b.id);
                        }}
                      >
                        <Target size={13} />
                      </button>
                    </span>
                  </td>
                </tr>
                {isOpen && (
                  <tr>
                    <td style={ovExpandTdStyle} colSpan={7}>
                      <div style={ovExpandBoxStyle}>
                        <HarnessDetailPanel
                          bundle={b}
                          endpoints={eps}
                          wires={wires}
                          onPersistGraph={(g) => onPersistGraph(b.id, g)}
                          onChangeBundle={(patch) => onChangeBundle(b.id, patch)}
                          onDeleteBundle={() => onDeleteBundle(b.id, b.name)}
                          onShowInSchematic={() => onShowInSchematic(b.id)}
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      <button style={{ ...primaryBtn, margin: 14 }} onClick={onAdd}>
        <Plus size={14} /> Add harness page
      </button>
    </div>
  );
}

/* ================================================================== *
 * Detail panel — inner Connectors / Wiring / BOM / Details tabs shown
 * inline inside an expanded Overview row (no separate page/navigation).
 * ================================================================== */

function HarnessDetailPanel({
  bundle,
  endpoints,
  wires,
  onPersistGraph,
  onChangeBundle,
  onDeleteBundle,
  onShowInSchematic,
}: {
  bundle: InterconnectBundle;
  endpoints: HarnessEndpoint[];
  wires: HarnessWire[];
  onPersistGraph: (graph: HarnessGraph) => void;
  onChangeBundle: (patch: Partial<InterconnectBundle>) => void;
  onDeleteBundle: () => void;
  onShowInSchematic: () => void;
}) {
  const [tab, setTab] = useState<'diagram' | 'connectors' | 'wiring' | 'bom' | 'details'>('diagram');
  const graph = useMemo(() => normalizeGraph(bundle.graph), [bundle.graph]);
  const tabLabel = (t: typeof tab) =>
    t === 'diagram'
      ? 'Diagram'
      : t === 'connectors'
        ? 'Connectors'
        : t === 'wiring'
          ? 'Wiring'
          : t === 'bom'
            ? 'BOM'
            : 'Details';

  return (
    <div style={formboardWrapStyle}>
      <div style={formToolbarStyle}>
        {(['diagram', 'connectors', 'wiring', 'bom', 'details'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{ ...viewTabStyle, ...(tab === t ? viewTabActiveStyle : {}) }}
          >
            {tabLabel(t)}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <button
          style={toolBtnStyle}
          onClick={onShowInSchematic}
          disabled={wires.length === 0}
          title="Jump to the schematic and highlight this harness's wires"
        >
          <Target size={13} /> Show in schematic
        </button>
        <button style={{ ...toolBtnStyle, color: '#dc2626' }} onClick={onDeleteBundle} title="Delete this harness">
          <Trash2 size={13} /> Delete harness
        </button>
      </div>
      {tab === 'diagram' && <HarnessDiagram wires={wires} />}
      {tab === 'connectors' && (
        <HarnessConnectorsView graph={graph} endpoints={endpoints} wires={wires} onPersist={onPersistGraph} />
      )}
      {tab === 'wiring' && <HarnessWiringView bundleName={bundle.name} wires={wires} graph={graph} />}
      {tab === 'bom' && <HarnessBomView bundleName={bundle.name} endpoints={endpoints} wires={wires} graph={graph} />}
      {tab === 'details' && <HarnessInfoPanel bundle={bundle} onChange={onChangeBundle} />}
    </div>
  );
}

/* ================================================================== *
 * Connector / pinout table view — one card per connector, each with
 * its auto-derived wire table (RapidHarness-style pinout list).
 * ================================================================== */

function HarnessConnectorsView({
  graph,
  endpoints,
  wires,
  onPersist,
}: {
  graph: HarnessGraph;
  endpoints: HarnessEndpoint[];
  wires: HarnessWire[];
  onPersist: (graph: HarnessGraph) => void;
}) {
  // The connector LIST is always the live schematic endpoints — no manual
  // add/sync/clean step. Only termination + contact PN are user-editable,
  // stored in a sidecar node that's created the first time it's touched.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const updateMeta = (ep: HarnessEndpoint, patch: Partial<Pick<HarnessGraphNode, 'termination' | 'contactPNs'>>) =>
    onPersist(upsertConnectorNode(graph, ep, patch));

  // Unique per mounted instance — multiple harnesses can be expanded in the
  // Overview accordion at once, and each renders its own <datalist>.
  const terminationsListId = `harness-terminations-${useId()}`;

  return (
    <div style={connectorsScrollStyle}>
      {endpoints.length === 0 && (
        <div style={emptyConnectorsStyle}>
          <Cable size={32} strokeWidth={1.4} />
          <div style={{ fontWeight: 700, marginTop: 8 }}>No connectors yet</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, maxWidth: 320, textAlign: 'center' }}>
            Assign wires to this harness in a schematic sheet — the connector list stays in sync
            automatically.
          </div>
        </div>
      )}
      {endpoints.map((ep) => {
        const key = endpointKey(ep.componentId, ep.connector);
        const node = findConnectorNode(graph, ep);
        const rows = connectorRows(ep.componentId, ep.component, ep.connector, wires);
        const isOpen = expanded.has(key);
        return (
          <div key={key} style={connectorCardStyle}>
            <div
              style={{
                ...connectorCardHeaderStyle,
                borderBottom: isOpen ? '1px solid var(--panel-border)' : 'none',
              }}
              onClick={() => toggleExpanded(key)}
            >
              {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <span style={connectorCardNameStyle}>{ep.connector || ep.component}</span>
              {ep.connector && <span style={connectorCardRefStyle}>{ep.component}</span>}
              <span style={connectorCardCountStyle}>
                {rows.length} wire{rows.length === 1 ? '' : 's'}
              </span>
              <CommitInput
                list={terminationsListId}
                value={node?.termination ?? ''}
                placeholder="Termination"
                onCommit={(v) => updateMeta(ep, { termination: v })}
                onClick={(e) => e.stopPropagation()}
                style={connectorCardTermStyle}
              />
            </div>
            {isOpen && (
              <table style={inlineTableTableStyle}>
                <colgroup>
                  <col style={{ width: 60 }} />
                  <col style={{ width: 56 }} />
                  <col />
                  <col style={{ width: 28 }} />
                  <col style={{ width: 52 }} />
                  <col style={{ width: 96 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={inlineThStyle}>Wire</th>
                    <th style={inlineThStyle}>Pin</th>
                    <th style={inlineThStyle}>To</th>
                    <th style={inlineThStyle}></th>
                    <th style={inlineThStyle}>Ga.</th>
                    <th style={inlineThStyle}>Contact PN</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td style={inlineTdStyle} colSpan={6}>
                        no linked wires
                      </td>
                    </tr>
                  )}
                  {rows.map((r, i) => (
                    <tr key={i}>
                      <td style={{ ...inlineTdStyle, color: 'var(--accent)', fontWeight: 700 }}>{r.wireNo}</td>
                      <td style={inlineTdStyle}>{r.pin}</td>
                      <td style={inlineTdWrapStyle}>{r.to}</td>
                      <td style={inlineTdStyle}>
                        {r.color ? <span style={{ ...swatchStyle, background: r.color }} title={r.color} /> : null}
                      </td>
                      <td style={inlineTdStyle}>{r.gauge}</td>
                      <td style={inlineTdStyle}>
                        <CommitInput
                          value={node?.contactPNs?.[r.pin] ?? ''}
                          placeholder="—"
                          onCommit={(v) =>
                            updateMeta(ep, { contactPNs: { ...(node?.contactPNs ?? {}), [r.pin]: v } })
                          }
                          style={contactPnInputStyle}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
      <datalist id={terminationsListId}>
        {TERMINATION_PRESETS.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>
    </div>
  );
}

/* ================================================================== *
 * Right info panel: auto connector pinout tables + bundle metadata
 * ================================================================== */

function HarnessInfoPanel({
  bundle,
  onChange,
}: {
  bundle: InterconnectBundle;
  onChange: (patch: Partial<InterconnectBundle>) => void;
}) {
  return (
    <div style={infoPanelStyle}>
      <div style={infoScrollStyle}>
        {/* metadata */}
        <section style={infoSectionStyle}>
          <div style={infoSectionTitleStyle}>Harness</div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
            <Field label="Name">
              <input style={infoInputStyle} value={bundle.name} onChange={(e) => onChange({ name: e.target.value })} />
            </Field>
            <Field label="Drawing #">
              <input
                style={infoInputStyle}
                value={bundle.drawingNumber ?? ''}
                onChange={(e) => onChange({ drawingNumber: e.target.value })}
              />
            </Field>
            <Field label="Revision">
              <input
                style={infoInputStyle}
                value={bundle.revision ?? ''}
                onChange={(e) => onChange({ revision: e.target.value })}
              />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
            <Field label="From">
              <input
                style={infoInputStyle}
                value={bundle.fromUnit}
                list="skema-subsystem-units"
                placeholder="e.g. Control Box"
                onChange={(e) => onChange({ fromUnit: e.target.value })}
              />
            </Field>
            <Field label="To">
              <input
                style={infoInputStyle}
                value={bundle.toUnit}
                list="skema-subsystem-units"
                placeholder="e.g. Robot"
                onChange={(e) => onChange({ toUnit: e.target.value })}
              />
            </Field>
            <Field label="Length">
              <input style={infoInputStyle} value={bundle.length} onChange={(e) => onChange({ length: e.target.value })} />
            </Field>
            <Field label="Conduit">
              <input style={infoInputStyle} value={bundle.conduit} onChange={(e) => onChange({ conduit: e.target.value })} />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Field label="Cables">
              <textarea
                style={{ ...infoInputStyle, minHeight: 48, resize: 'vertical' }}
                value={bundle.cables}
                onChange={(e) => onChange({ cables: e.target.value })}
              />
            </Field>
            <Field label="Notes">
              <textarea
                style={{ ...infoInputStyle, minHeight: 48, resize: 'vertical' }}
                value={bundle.notes}
                onChange={(e) => onChange({ notes: e.target.value })}
              />
            </Field>
          </div>
        </section>

        {/* title block — approvals */}
        <section style={infoSectionStyle}>
          <div style={infoSectionTitleStyle}>Title block</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
            <Field label="Drawn by">
              <input style={infoInputStyle} value={bundle.drawnBy ?? ''} onChange={(e) => onChange({ drawnBy: e.target.value })} />
            </Field>
            <Field label="Eng approved">
              <input style={infoInputStyle} value={bundle.checkedBy ?? ''} onChange={(e) => onChange({ checkedBy: e.target.value })} />
            </Field>
            <Field label="Mfg approved">
              <input style={infoInputStyle} value={bundle.approvedBy ?? ''} onChange={(e) => onChange({ approvedBy: e.target.value })} />
            </Field>
            <Field label="QA">
              <input style={infoInputStyle} value={bundle.qaBy ?? ''} onChange={(e) => onChange({ qaBy: e.target.value })} />
            </Field>
          </div>
        </section>

        {/* revision history */}
        <section style={infoSectionStyle}>
          <div style={infoSectionTitleStyle}>
            Revisions
            <button
              style={revAddBtnStyle}
              onClick={() => {
                const next = [...(bundle.revisions ?? [])];
                const lastRev = next.at(-1)?.rev ?? '';
                const nextRev = lastRev
                  ? String.fromCharCode(lastRev.charCodeAt(0) + 1)
                  : 'A';
                next.push({ id: nanoid(8), rev: nextRev, date: '', author: '', description: '' });
                onChange({ revisions: next });
              }}
              title="Add revision row"
            >
              <Plus size={12} /> Add
            </button>
          </div>
          <table style={revTableStyle}>
            <thead>
              <tr>
                <th style={revThStyle}>Rev</th>
                <th style={revThStyle}>Date</th>
                <th style={revThStyle}>By</th>
                <th style={revThStyle}>Description</th>
                <th style={revThStyle}></th>
              </tr>
            </thead>
            <tbody>
              {(bundle.revisions ?? []).length === 0 && (
                <tr>
                  <td style={revTdStyle} colSpan={5}>
                    No revisions yet.
                  </td>
                </tr>
              )}
              {(bundle.revisions ?? []).map((rev) => {
                const patchRev = (patch: Partial<typeof rev>) => {
                  const next = (bundle.revisions ?? []).map((r) => (r.id === rev.id ? { ...r, ...patch } : r));
                  onChange({ revisions: next });
                };
                return (
                  <tr key={rev.id}>
                    <td style={revTdStyle}>
                      <input style={revInputStyle} value={rev.rev} onChange={(e) => patchRev({ rev: e.target.value })} />
                    </td>
                    <td style={revTdStyle}>
                      <input style={revInputStyle} value={rev.date} placeholder="—" onChange={(e) => patchRev({ date: e.target.value })} />
                    </td>
                    <td style={revTdStyle}>
                      <input style={revInputStyle} value={rev.author} placeholder="—" onChange={(e) => patchRev({ author: e.target.value })} />
                    </td>
                    <td style={revTdStyle}>
                      <input style={revInputStyle} value={rev.description} placeholder="—" onChange={(e) => patchRev({ description: e.target.value })} />
                    </td>
                    <td style={revTdStyle}>
                      <button
                        style={revDelBtnStyle}
                        onClick={() => onChange({ revisions: (bundle.revisions ?? []).filter((r) => r.id !== rev.id) })}
                        title="Remove row"
                      >
                        <Trash2 size={11} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: 'block', marginBottom: 8 }}>
      <span style={infoLabelStyle}>{label}</span>
      {children}
    </label>
  );
}

/* ================================================================== *
 * Wiring table view — full connection list
 * ================================================================== */

function HarnessWiringView({
  bundleName,
  wires,
  graph,
}: {
  bundleName: string;
  wires: HarnessWire[];
  graph: HarnessGraph;
}) {
  const rows = [...wires].sort((a, b) =>
    `${a.fromComp}${a.fromConnector}${a.fromPin}`.localeCompare(
      `${b.fromComp}${b.fromConnector}${b.fromPin}`,
      undefined,
      { numeric: true },
    ),
  );
  // Contact PN lookup — prefer the stable component-node id (survives
  // renames); only fall back to the label for legacy nodes with no id.
  const contactPN = new Map<string, string>();
  for (const n of graph.nodes) {
    if (n.type !== 'connector' || !n.contactPNs) continue;
    for (const [pin, pn] of Object.entries(n.contactPNs)) {
      if (!pn) continue;
      if (n.refNodeId) contactPN.set(`id:${n.refNodeId}||${n.refConnector ?? ''}||${pin}`, pn);
      else if (n.refComponent) contactPN.set(`lb:${n.refComponent}||${n.refConnector ?? ''}||${pin}`, pn);
    }
  }
  const pnFor = (compId: string, comp: string, conn: string, pin: string) =>
    contactPN.get(`id:${compId}||${conn}||${pin}`) ?? contactPN.get(`lb:${comp}||${conn}||${pin}`) ?? '';
  return (
    <div style={tableViewWrapStyle}>
      <div style={tableViewHeaderStyle}>
        Wiring table — {bundleName} <span style={tableViewCountStyle}>{rows.length} wires</span>
      </div>
      <div style={tableViewScrollStyle}>
        <table style={bigTableStyle}>
          <thead>
            <tr>
              <th style={bigThStyle}>#</th>
              <th style={bigThStyle}>From</th>
              <th style={bigThStyle}>From Contact PN</th>
              <th style={bigThStyle}>To</th>
              <th style={bigThStyle}>To Contact PN</th>
              <th style={bigThStyle}>Conductor</th>
              <th style={bigThStyle}>Color</th>
              <th style={bigThStyle}>Gauge</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td style={bigTdStyle} colSpan={8}>
                  No wires assigned to this harness yet.
                </td>
              </tr>
            )}
            {rows.map((w, i) => (
              <tr key={w.edgeId}>
                <td style={bigTdMutedStyle}>{i + 1}</td>
                <td style={bigTdStyle}>
                  {w.fromComp}
                  {w.fromConnector ? `.${w.fromConnector}` : ''}
                  {w.fromPin ? `.${w.fromPin}` : ''}
                </td>
                <td style={bigTdMutedStyle}>{pnFor(w.fromCompId, w.fromComp, w.fromConnector, w.fromPin)}</td>
                <td style={bigTdStyle}>
                  {w.toComp}
                  {w.toConnector ? `.${w.toConnector}` : ''}
                  {w.toPin ? `.${w.toPin}` : ''}
                </td>
                <td style={bigTdMutedStyle}>{pnFor(w.toCompId, w.toComp, w.toConnector, w.toPin)}</td>
                <td style={bigTdStyle}>{w.label}</td>
                <td style={bigTdStyle}>
                  {w.color ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ ...swatchStyle, background: w.color }} />
                      {w.color}
                    </span>
                  ) : (
                    ''
                  )}
                </td>
                <td style={bigTdStyle}>{w.gauge}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ================================================================== *
 * BOM view — connectors, splices, coverings, wires
 * ================================================================== */

interface BomRow {
  type: string;
  description: string;
  detail: string;
  qty: number;
  color?: string;
}

function HarnessBomView({
  bundleName,
  endpoints,
  wires,
  graph,
}: {
  bundleName: string;
  endpoints: HarnessEndpoint[];
  wires: HarnessWire[];
  graph: HarnessGraph;
}) {
  const rows: BomRow[] = [];

  // Connectors — one per endpoint; termination pulled from the matching graph
  // node (by stable node id; label fallback only applies to legacy nodes
  // that predate refNodeId, so a stale node can't shadow a live connector
  // that happens to share its old label).
  const termByRef = new Map<string, string>();
  for (const n of graph.nodes) {
    if (n.type === 'connector' && n.termination) {
      if (n.refNodeId) termByRef.set(`id:${n.refNodeId}||${n.refConnector ?? ''}`, n.termination);
      else if (n.refComponent) termByRef.set(`lb:${n.refComponent}||${n.refConnector ?? ''}`, n.termination);
    }
  }
  const termFor = (ep: HarnessEndpoint) =>
    termByRef.get(`id:${ep.componentId}||${ep.connector}`) ??
    termByRef.get(`lb:${ep.component}||${ep.connector}`);
  // Live sidecar node ids — only these contribute contact-PN counts below,
  // so a deleted/renamed-away connector's old contact PNs stop being
  // counted (they aren't deleted, just no longer live).
  const liveConnectorIds = new Set(
    endpoints.map((ep) => findConnectorNode(graph, ep)?.id).filter((id): id is string => Boolean(id)),
  );
  for (const ep of endpoints) {
    const term = termFor(ep);
    rows.push({
      type: 'Connector',
      description: ep.connector || ep.component,
      detail: `${ep.component} · ${ep.wireCount} wire${ep.wireCount === 1 ? '' : 's'}${
        term ? ` · ${term}` : ''
      }`,
      qty: 1,
    });
  }

  // Splices / junctions.
  const junctions = graph.nodes.filter((n) => n.type === 'junction');
  for (const j of junctions) {
    rows.push({ type: 'Splice', description: j.label || 'Junction', detail: 'branch / splice point', qty: 1 });
  }

  // Coverings — grouped by name.
  const coverCount = new Map<string, number>();
  for (const s of graph.segments) {
    if (s.covering) coverCount.set(s.covering, (coverCount.get(s.covering) ?? 0) + 1);
  }
  for (const [name, qty] of coverCount.entries()) {
    rows.push({ type: 'Covering', description: name, detail: `${qty} segment${qty === 1 ? '' : 's'}`, qty });
  }

  // Wires — grouped by gauge + color.
  const wireGroups = new Map<string, { gauge: string; color: string; qty: number }>();
  for (const w of wires) {
    const key = `${w.gauge}||${w.color}`;
    const g = wireGroups.get(key);
    if (g) g.qty += 1;
    else wireGroups.set(key, { gauge: w.gauge, color: w.color, qty: 1 });
  }
  for (const g of wireGroups.values()) {
    rows.push({
      type: 'Wire',
      description: [g.gauge, g.color].filter(Boolean).join(' ') || 'Wire',
      detail: 'conductor',
      qty: g.qty,
      color: g.color,
    });
  }

  // Contacts — grouped by part number, live connectors only.
  const contactCount = new Map<string, number>();
  for (const n of graph.nodes) {
    if (n.type !== 'connector' || !n.contactPNs || !liveConnectorIds.has(n.id)) continue;
    for (const pn of Object.values(n.contactPNs)) {
      if (pn) contactCount.set(pn, (contactCount.get(pn) ?? 0) + 1);
    }
  }
  for (const [pn, qty] of contactCount) {
    rows.push({ type: 'Contact', description: pn, detail: 'crimp contact', qty });
  }

  return (
    <div style={tableViewWrapStyle}>
      <div style={tableViewHeaderStyle}>
        Bill of materials — {bundleName} <span style={tableViewCountStyle}>{rows.length} line items</span>
      </div>
      <div style={tableViewScrollStyle}>
        <table style={bigTableStyle}>
          <thead>
            <tr>
              <th style={bigThStyle}>#</th>
              <th style={bigThStyle}>Type</th>
              <th style={bigThStyle}>Description</th>
              <th style={bigThStyle}>Detail</th>
              <th style={bigThStyle}>Qty</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td style={bigTdStyle} colSpan={5}>
                  Nothing to list yet — sync from the schematic or add connectors.
                </td>
              </tr>
            )}
            {rows.map((r, i) => (
              <tr key={i}>
                <td style={bigTdMutedStyle}>{i + 1}</td>
                <td style={bigTdStyle}>
                  <span style={bomTypeStyle}>{r.type}</span>
                </td>
                <td style={bigTdStyle}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {r.color ? <span style={{ ...swatchStyle, background: r.color }} /> : null}
                    {r.description}
                  </span>
                </td>
                <td style={bigTdMutedStyle}>{r.detail}</td>
                <td style={bigTdStyle}>{r.qty}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ================================================================== *
 * Styles
 * ================================================================== */

const shellStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--bg-canvas)',
  overflow: 'hidden',
};

const topBarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 16px',
  background: 'var(--panel)',
  borderBottom: '1px solid var(--panel-border)',
};

const secondaryBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  background: 'var(--panel)',
  color: 'var(--text)',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 9px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
};

const primaryBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--accent)',
  color: 'white',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 12px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  boxShadow: '0 1px 2px rgba(37, 99, 235, 0.25)',
};

const searchWrapStyle: CSSProperties = { position: 'relative', flex: 1, maxWidth: 360 };
const searchIconStyle: CSSProperties = {
  position: 'absolute',
  left: 10,
  top: '50%',
  transform: 'translateY(-50%)',
  color: 'var(--text-muted)',
  pointerEvents: 'none',
};
const searchInputStyle: CSSProperties = {
  width: '100%',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 10px 6px 28px',
  fontSize: 12,
  background: 'var(--panel)',
  color: 'var(--text)',
  outline: 'none',
  boxSizing: 'border-box',
};
const countStyle: CSSProperties = {
  fontSize: 11,
  color: 'var(--text-muted)',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const workspaceStyle: CSSProperties = { flex: 1, display: 'flex', minHeight: 0 };

const tabAccentStyle: CSSProperties = { width: 5, alignSelf: 'stretch', borderRadius: 4, flexShrink: 0 };

const formboardWrapStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
};

const formToolbarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  padding: '7px 10px',
  borderBottom: '1px solid var(--panel-border)',
  background: 'var(--panel)',
  flexWrap: 'wrap',
};
const overviewWrapStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflow: 'auto',
  background: 'var(--bg-canvas)',
  display: 'flex',
  flexDirection: 'column',
};
const overviewTableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 12.5,
  background: 'var(--panel)',
};
const ovThStyle: CSSProperties = {
  textAlign: 'left',
  padding: '9px 12px',
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  background: 'linear-gradient(var(--chrome-top), var(--chrome-bottom))',
  borderBottom: '1px solid var(--chrome-border)',
  position: 'sticky',
  top: 0,
};
const ovThNumStyle: CSSProperties = { ...ovThStyle, textAlign: 'right' };
const ovRowStyle: CSSProperties = {
  cursor: 'pointer',
  borderBottom: '1px solid var(--panel-border)',
};
const ovTdStyle: CSSProperties = {
  padding: '8px 12px',
  color: 'var(--text)',
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
};
const ovTdNumStyle: CSSProperties = {
  ...ovTdStyle,
  textAlign: 'right',
  fontFamily: 'ui-monospace, Consolas, monospace',
};
const ovChevronStyle: CSSProperties = { verticalAlign: 'middle', marginRight: 6, color: 'var(--text-muted)' };
const showInSchematicBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 22,
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel)',
  color: 'var(--accent)',
  cursor: 'pointer',
  padding: 0,
};
const ovExpandTdStyle: CSSProperties = {
  padding: 0,
  background: 'var(--bg-canvas)',
  borderBottom: '1px solid var(--panel-border)',
  whiteSpace: 'normal',
};
const ovExpandBoxStyle: CSSProperties = { display: 'flex', maxHeight: 520, overflow: 'auto' };
const toolBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  minHeight: 26,
  padding: '4px 8px',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel)',
  color: 'var(--text)',
  fontSize: 11,
  fontWeight: 700,
  cursor: 'pointer',
};
/* ---- connector cards ---- */
const connectorsScrollStyle: CSSProperties = {
  flex: 1,
  overflow: 'auto',
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  background: 'var(--bg-canvas)',
};
const emptyConnectorsStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--text-muted)',
  padding: 40,
};
const connectorCardStyle: CSSProperties = {
  background: 'var(--panel)',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius)',
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(15,23,42,0.08)',
};
const connectorCardHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 10px',
  background: 'var(--panel-alt)',
  flexWrap: 'wrap',
  cursor: 'pointer',
  userSelect: 'none',
  color: 'var(--text-muted)',
};
const connectorCardCountStyle: CSSProperties = {
  fontSize: 10.5,
  fontWeight: 700,
  color: 'var(--text-muted)',
  whiteSpace: 'nowrap',
};
const connectorCardNameStyle: CSSProperties = {
  color: 'var(--text)',
  fontSize: 13,
  fontWeight: 800,
};
const connectorCardRefStyle: CSSProperties = {
  fontSize: 11,
  color: 'var(--text-muted)',
  fontWeight: 600,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
const connectorCardTermStyle: CSSProperties = {
  border: '1px solid var(--panel-border)',
  borderRadius: 5,
  background: 'var(--panel)',
  color: 'var(--text)',
  fontSize: 11,
  padding: '4px 8px',
  outline: 'none',
  marginLeft: 'auto',
  minWidth: 140,
  fontFamily: 'inherit',
};
const contactPnInputStyle: CSSProperties = {
  width: 78,
  border: '1px solid transparent',
  borderRadius: 3,
  background: 'var(--panel-alt)',
  color: 'var(--text)',
  fontSize: 10,
  padding: '1px 4px',
  outline: 'none',
  fontFamily: 'inherit',
};

const infoPanelStyle: CSSProperties = {
  width: '100%',
  background: 'var(--panel-alt)',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
};
const infoScrollStyle: CSSProperties = { flex: 1, overflowY: 'auto', padding: 12 };
const infoSectionStyle: CSSProperties = { marginBottom: 18 };
const infoSectionTitleStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 11,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  color: 'var(--text-muted)',
  marginBottom: 8,
};
const infoLabelStyle: CSSProperties = {
  display: 'block',
  marginBottom: 3,
  fontSize: 10,
  fontWeight: 800,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
};
const infoInputStyle: CSSProperties = {
  width: '100%',
  border: '1px solid var(--panel-border)',
  borderRadius: 5,
  background: 'var(--panel)',
  color: 'var(--text)',
  padding: '5px 7px',
  fontSize: 12,
  boxSizing: 'border-box',
  outline: 'none',
  fontFamily: 'inherit',
};
/* ---- inline connector tables ---- */
const inlineTableTableStyle: CSSProperties = {
  width: '100%',
  tableLayout: 'fixed',
  borderCollapse: 'collapse',
  fontSize: 10.5,
};
const inlineThStyle: CSSProperties = {
  textAlign: 'left',
  padding: '4px 6px',
  borderBottom: '1px solid var(--panel-border)',
  color: 'var(--text-muted)',
  fontWeight: 800,
  background: 'var(--panel)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const inlineTdStyle: CSSProperties = {
  padding: '4px 6px',
  borderBottom: '1px solid var(--panel-border)',
  color: 'var(--text)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const inlineTdWrapStyle: CSSProperties = {
  ...inlineTdStyle,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflow: 'visible',
  textOverflow: 'clip',
};
const swatchStyle: CSSProperties = {
  display: 'inline-block',
  width: 22,
  height: 11,
  borderRadius: 2,
  border: '1px solid rgba(0,0,0,0.25)',
  verticalAlign: 'middle',
};

/* ---- view tabs ---- */
const viewTabStyle: CSSProperties = {
  minWidth: 64,
  height: 26,
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: 'transparent',
  borderRadius: 5,
  background: 'transparent',
  color: 'var(--text-muted)',
  fontSize: 11,
  fontWeight: 800,
  cursor: 'pointer',
};
const viewTabActiveStyle: CSSProperties = {
  background: 'var(--accent-soft)',
  color: 'var(--accent)',
  borderColor: 'var(--accent-border)',
};

/* ---- table views (wiring / bom) ---- */
const tableViewWrapStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--bg-canvas)',
};
const tableViewHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 10,
  padding: '10px 16px',
  borderBottom: '1px solid var(--panel-border)',
  background: 'var(--panel)',
  fontSize: 13,
  fontWeight: 800,
  color: 'var(--text)',
};
const tableViewCountStyle: CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' };
const tableViewScrollStyle: CSSProperties = { flex: 1, overflow: 'auto', padding: 16 };
const bigTableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 12,
  background: 'var(--panel)',
  border: '1px solid var(--panel-border)',
};
const bigThStyle: CSSProperties = {
  position: 'sticky',
  top: 0,
  textAlign: 'left',
  padding: '7px 10px',
  borderBottom: '2px solid var(--panel-border)',
  background: 'var(--panel-alt)',
  color: 'var(--text-muted)',
  fontWeight: 800,
  whiteSpace: 'nowrap',
};
const bigTdStyle: CSSProperties = {
  padding: '6px 10px',
  borderBottom: '1px solid var(--panel-border)',
  color: 'var(--text)',
  verticalAlign: 'top',
};
const bigTdMutedStyle: CSSProperties = { ...bigTdStyle, color: 'var(--text-muted)' };
const bomTypeStyle: CSSProperties = {
  display: 'inline-block',
  padding: '1px 7px',
  borderRadius: 999,
  background: 'var(--accent-soft)',
  color: 'var(--accent)',
  fontSize: 10,
  fontWeight: 800,
};

/* ---- revisions table (details panel) ---- */
const revAddBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  marginLeft: 'auto',
  border: '1px solid var(--panel-border)',
  borderRadius: 5,
  background: 'var(--panel)',
  color: 'var(--text)',
  padding: '2px 7px',
  fontSize: 10,
  fontWeight: 800,
  cursor: 'pointer',
  textTransform: 'none',
};
const revTableStyle: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 11 };
const revThStyle: CSSProperties = {
  textAlign: 'left',
  padding: '2px 4px',
  borderBottom: '1px solid var(--panel-border)',
  color: 'var(--text-muted)',
  fontWeight: 800,
};
const revTdStyle: CSSProperties = { padding: '2px 3px', borderBottom: '1px solid var(--panel-border)' };
const revInputStyle: CSSProperties = {
  width: '100%',
  border: '1px solid transparent',
  borderRadius: 4,
  background: 'var(--panel-alt)',
  color: 'var(--text)',
  padding: '3px 5px',
  fontSize: 11,
  boxSizing: 'border-box',
  outline: 'none',
  fontFamily: 'inherit',
};
const revDelBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  border: 'none',
  background: 'transparent',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  padding: 2,
};
